import {useEffect} from 'react';
import {useForm, useFieldArray, Controller} from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from 'app/gakki/components/Dialog';
import Button, {ButtonGroup} from 'components/Button';
import SubHeader from 'app/gakki/components/SubHeader';
import {
  TimesInput,
  TimesInputNumber,
  DatePicker,
  ErrorMessage,
} from 'components/form-items';
import {FormItem} from '@mayo/mayo-ui-gakki';
import Select from '@common/components/Select';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IconButton} from 'components/Button';
import {DateTime} from 'luxon';
import BillingDateInfo from './billing-date-info';
import cx from 'classnames';
import {DevTool} from '@hookform/devtools';
import {CustomIconButton} from '@common/components/Button';

const defaultAnnualLeavesField = {
  availableDuration: {
    start: null,
    end: null,
  },
  availableTime: null,
  billingDate: null,
};

const defaultCompensatoryLeavesField = {
  availableDuration: {
    start: null,
    end: null,
  },
  availableTime: null,
};
const yearOptions = [
  new Date().getFullYear() + 1,
  new Date().getFullYear(),
  new Date().getFullYear() - 1,
  new Date().getFullYear() - 2,
  new Date().getFullYear() - 3,
].map(y => ({value: y, label: y}));
const Form = (props: any) => {
  const {onClose, onSubmit, defaultValues} = props;
  const {
    control,
    formState,
    errors,
    handleSubmit,
    watch,
    trigger,
    register,
    setValue,
  } = useForm({
    defaultValues,
  });
  const remainingAnnualLeavesField = useFieldArray({
    control,
    name: 'remainingAnnualLeaves',
  });

  const remainingAnnualLeavesValues = watch('remainingAnnualLeaves');
  const remainingAnnualLeavesSelectedYear: number[] =
    remainingAnnualLeavesValues
      .map((l: any) => l.year)
      .filter((y?: number) => y);
  const remainingCompensatoryLeavesField = useFieldArray({
    control,
    name: 'remainingCompensatoryLeaves',
  });
  const remainingCompensatoryLeavesValues = watch(
    'remainingCompensatoryLeaves',
  );
  const remainingCompensatoryLeavesSelectedYear: number[] =
    remainingCompensatoryLeavesValues
      .map((l: any) => l.year)
      .filter((y?: number) => y);
  useEffect(() => {
    if (!defaultValues) {
      if (remainingAnnualLeavesField?.fields?.length === 0) {
        remainingAnnualLeavesField?.append(defaultAnnualLeavesField);
      }
      if (remainingCompensatoryLeavesField?.fields?.length === 0) {
        remainingCompensatoryLeavesField?.append(
          defaultCompensatoryLeavesField,
        );
      }
    }
  }, [remainingAnnualLeavesField, remainingCompensatoryLeavesField]);
  const transferDateObj = (d: any) => (d ? new Date(d) : undefined);
  const currentYear = new Date().getFullYear();
  const remainingPersonalLeave = watch('remainingPersonalLeave');
  const remainingSickLeave = watch('remainingSickLeave');

  return (
    <Dialog
      isOpen
      className={'!min-w-[920px] !max-w-none'}
      aria-label="remain-leave-dialog"
    >
      <DialogTitle
        onClose={onClose}
        title={`${defaultValues.employee.jobNo}/${defaultValues.employee.name}`}
        customStyle={{fontWeight: 500}}
      />
      <DialogContent className="text-[13px] flex-col !px-0 !pt-0 !pb-[30px]">
        <DevTool control={control} />
        <input name="seqNo" hidden ref={register} />
        <input name="employee.id" hidden ref={register} />
        <input name="employee.name" hidden ref={register} />
        <input name="employee.jobNo" hidden ref={register} />
        <SubHeader>剩餘特休</SubHeader>
        <div>
          {remainingAnnualLeavesField?.fields?.map((field, index) => {
            return (
              <div
                key={field.id}
                className={
                  'grid grid-cols-[130px,130px,130px,256px,185px] gap-5 [&:not(:first-child)]:mt-5'
                }
              >
                <FormItem
                  label="年度"
                  htmlFor={`remainingAnnualLeavesYear_${index}`}
                >
                  <Controller
                    control={control}
                    render={({onChange, value}) => (
                      <Select
                        name={`remainingAnnualLeavesYear_${index}`}
                        id={`remainingAnnualLeavesYear_${index}`}
                        options={yearOptions.filter(
                          o =>
                            !remainingAnnualLeavesSelectedYear.some(
                              y => y === o.value,
                            ),
                        )}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingAnnualLeaves?.[index]?.year
                        }
                        onChange={(option: {value: string}) =>
                          onChange(option.value)
                        }
                        value={value ? {value: value, label: value} : null}
                      />
                    )}
                    name={`remainingAnnualLeaves.${index}.year`}
                    rules={{
                      validate: v => {
                        const rowData = watch(`remainingAnnualLeaves.${index}`);
                        return Boolean(rowData?.availableDuration?.start) ||
                          Boolean(rowData?.availableDuration?.end) ||
                          Boolean(rowData?.availableTime) ||
                          Boolean(rowData?.billingDate)
                          ? Boolean(v) || '請填寫完整資訊'
                          : true;
                      },
                    }}
                  />
                </FormItem>
                <FormItem
                  label="可用期間(起)"
                  htmlFor={`remainingAnnualLeavesStart_${index}`}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <DatePicker
                        id={`remainingAnnualLeavesStart_${index}`}
                        selectsStart
                        dateFormat="yyyy/MM/dd"
                        selected={transferDateObj(value)}
                        onChange={async (date: Date) => {
                          onChange(
                            date
                              ? DateTime.fromJSDate(date).toLocaleString()
                              : null,
                          );
                          if (formState.isSubmitted) {
                            await trigger(
                              `remainingAnnualLeaves.${index}.availableDuration.end`,
                            );
                            await trigger(
                              `remainingAnnualLeaves.${index}.availableTime`,
                            );
                            await trigger(
                              `remainingAnnualLeaves.${index}.billingDate`,
                            );
                          }
                        }}
                        startDate={transferDateObj(value)}
                        endDate={transferDateObj(
                          watch(
                            `remainingAnnualLeaves.${index}.availableDuration.end`,
                          ),
                        )}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingAnnualLeaves?.[index]
                            ?.availableDuration?.start
                        }
                      />
                    )}
                    name={`remainingAnnualLeaves.${index}.availableDuration.start`}
                    control={control}
                    rules={{
                      validate: v => {
                        const rowData = watch(`remainingAnnualLeaves.${index}`);
                        return Boolean(rowData?.availableDuration?.end) ||
                          Boolean(rowData?.year) ||
                          Boolean(rowData?.availableTime) ||
                          Boolean(rowData?.billingDate)
                          ? Boolean(v) || '請填寫完整資訊'
                          : true;
                      },
                    }}
                    defaultValue={field.availableDuration.start}
                  />
                  {errors?.remainingAnnualLeaves?.[index]?.availableDuration
                    ?.start && (
                    <ErrorMessage>
                      {
                        errors?.remainingAnnualLeaves?.[index]
                          ?.availableDuration?.start.message
                      }
                    </ErrorMessage>
                  )}
                </FormItem>
                <FormItem
                  label="可用期間(迄)"
                  htmlFor={`remainingAnnualLeavesEnd_${index}`}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <DatePicker
                        id={`remainingAnnualLeavesEnd_${index}`}
                        selectsEnd
                        dateFormat="yyyy/MM/dd"
                        selected={transferDateObj(value)}
                        onChange={async (date: Date) => {
                          onChange(
                            date
                              ? DateTime.fromJSDate(date).toLocaleString()
                              : null,
                          );
                          if (formState.isSubmitted) {
                            await trigger(
                              `remainingAnnualLeaves.${index}.availableDuration.start`,
                            );
                            await trigger(
                              `remainingAnnualLeaves.${index}.availableTime`,
                            );
                            await trigger(
                              `remainingAnnualLeaves.${index}.billingDate`,
                            );
                          }
                        }}
                        startDate={transferDateObj(
                          watch(
                            `remainingAnnualLeaves.${index}.availableDuration.start`,
                          ),
                        )}
                        endDate={transferDateObj(value)}
                        minDate={transferDateObj(
                          watch(
                            `remainingAnnualLeaves.${index}.availableDuration.start`,
                          ),
                        )}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingAnnualLeaves?.[index]
                            ?.availableDuration?.end
                        }
                      />
                    )}
                    name={`remainingAnnualLeaves.${index}.availableDuration.end`}
                    control={control}
                    rules={{
                      validate: {
                        isAfterStart: v => {
                          const startDate = watch(
                            `remainingAnnualLeaves.${index}.availableDuration.start`,
                          );
                          let startDateObj = transferDateObj(startDate);
                          let endDateObj = transferDateObj(v);
                          if (startDateObj && endDateObj) {
                            return (
                              DateTime.fromJSDate(startDateObj) <=
                                DateTime.fromJSDate(endDateObj) ||
                              '迄日不可早於起始日'
                            );
                          }
                          return true;
                        },
                        isAllFillIn: v => {
                          const rowData = watch(
                            `remainingAnnualLeaves.${index}`,
                          );
                          return Boolean(rowData?.availableDuration?.start) ||
                            Boolean(rowData?.year) ||
                            Boolean(rowData?.availableTime) ||
                            Boolean(rowData?.billingDate)
                            ? Boolean(v) || '請填寫完整資訊'
                            : true;
                        },
                      },
                    }}
                    defaultValue={field.availableDuration.end}
                  />
                  {errors?.remainingAnnualLeaves?.[index]?.availableDuration
                    ?.end && (
                    <ErrorMessage>
                      {
                        errors?.remainingAnnualLeaves?.[index]
                          ?.availableDuration?.end.message
                      }
                    </ErrorMessage>
                  )}
                </FormItem>
                <FormItem
                  label="可用時數"
                  htmlFor={`remainingAnnualLeavesAvailableTime_${index}`}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <TimesInputNumber
                        autoComplete
                        onChange={async time => {
                          onChange(time);
                          if (formState.isSubmitted) {
                            await trigger(
                              `remainingAnnualLeaves.${index}.availableDuration.start`,
                            );
                            await trigger(
                              `remainingAnnualLeaves.${index}.availableDuration.end`,
                            );
                            await trigger(
                              `remainingAnnualLeaves.${index}.billingDate`,
                            );
                          }
                        }}
                        value={value}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingAnnualLeaves?.[index]?.availableTime
                            ?.message
                        }
                        hourOptions={{
                          min: 0,
                          max: 999,
                          step: 1,
                        }}
                      />
                    )}
                    name={`remainingAnnualLeaves.${index}.availableTime`}
                    control={control}
                    rules={{
                      validate: v => {
                        const rowData = watch(`remainingAnnualLeaves.${index}`);
                        return Boolean(rowData?.availableDuration?.start) ||
                          Boolean(rowData?.availableDuration?.end) ||
                          Boolean(rowData?.year) ||
                          Boolean(rowData?.billingDate)
                          ? Boolean(v) || '請填寫完整資訊'
                          : true;
                      },
                    }}
                    defaultValue={field.availableTime}
                  />
                </FormItem>
                <FormItem
                  label={
                    <div className="flex items-center gap-[5px]">
                      計薪基準日
                      <BillingDateInfo />
                    </div>
                  }
                  htmlFor={`remainingAnnualLeavesBillingDate_${index}`}
                >
                  <div className="flex justify-start items-center">
                    <div className="w-[135px]">
                      <Controller
                        render={({onChange, value}) => (
                          <DatePicker
                            dateFormat="yyyy/MM/dd"
                            selected={transferDateObj(value)}
                            onChange={async (date: Date) => {
                              onChange(
                                date
                                  ? DateTime.fromJSDate(date).toLocaleString()
                                  : null,
                              );
                              if (formState.isSubmitted) {
                                await trigger(
                                  `remainingAnnualLeaves.${index}.availableDuration.start`,
                                );
                                await trigger(
                                  `remainingAnnualLeaves.${index}.availableDuration.end`,
                                );
                                await trigger(
                                  `remainingAnnualLeaves.${index}.availableTime`,
                                );
                              }
                            }}
                            error={
                              formState.isSubmitted &&
                              errors?.remainingAnnualLeaves?.[index]
                                ?.billingDate
                            }
                            minDate={transferDateObj(
                              watch(
                                `remainingAnnualLeaves.${index}.availableDuration.start`,
                              ),
                            )}
                          />
                        )}
                        name={`remainingAnnualLeaves.${index}.billingDate`}
                        control={control}
                        rules={{
                          validate: {
                            isAfterStart: v => {
                              const startDate = watch(
                                `remainingAnnualLeaves.${index}.availableDuration.start`,
                              );
                              let startDateObj = transferDateObj(startDate);
                              let billingDateObj = transferDateObj(v);
                              if (startDateObj && billingDateObj) {
                                return (
                                  DateTime.fromJSDate(startDateObj) <=
                                    DateTime.fromJSDate(billingDateObj) ||
                                  '計薪基準日不可早於起始日'
                                );
                              }
                              return true;
                            },
                            isAllFillIn: v => {
                              const rowData = watch(
                                `remainingAnnualLeaves.${index}`,
                              );
                              return Boolean(
                                rowData?.availableDuration?.start,
                              ) ||
                                Boolean(rowData?.availableDuration?.end) ||
                                Boolean(rowData?.year) ||
                                Boolean(rowData?.availableTime)
                                ? Boolean(v) || '請填寫完整資訊'
                                : true;
                            },
                          },
                        }}
                        defaultValue={field.billingDate}
                      />
                    </div>
                    {remainingAnnualLeavesField?.fields?.length > 1 && (
                      <CustomIconButton
                        onClick={() => remainingAnnualLeavesField.remove(index)}
                        data-test-id={'remove-btn'}
                        icon="trash"
                        className="ml-[15px]"
                      />
                    )}
                  </div>
                  {errors?.remainingAnnualLeaves?.[index]?.billingDate && (
                    <ErrorMessage>
                      {
                        errors?.remainingAnnualLeaves?.[index]?.billingDate
                          .message
                      }
                    </ErrorMessage>
                  )}
                </FormItem>
              </div>
            );
          })}
        </div>
        <IconButton
          className={
            'w-[160px] text-[#353d45] border-[#353d45] hover:border-brand mt-3.5'
          }
          onClick={() =>
            remainingAnnualLeavesField.append(defaultAnnualLeavesField)
          }
        >
          <span
            style={{borderColor: 'inherit'}}
            className={cx(
              'w-[28px] h-[28px]',
              'flex items-center justify-center',
            )}
          >
            <FontAwesomeIcon
              icon={['fal', 'plus']}
              className={'!m-0 !text-xl'}
            />
          </span>
          新增剩餘特休
        </IconButton>
        <SubHeader>剩餘補休</SubHeader>
        <div>
          {remainingCompensatoryLeavesField?.fields?.map((field, index) => {
            return (
              <div
                key={field.id}
                className={
                  'grid gap-5 grid-cols-[130px,130px,130px,256px,20px] [&:not(:first-child)]:mt-5'
                }
              >
                <FormItem
                  label="年度"
                  htmlFor={`remainingCompensatoryLeavesYear_${index}`}
                >
                  <Controller
                    control={control}
                    render={({onChange, value}) => (
                      <Select
                        name={`remainingCompensatoryLeavesYear_${index}`}
                        id={`remainingCompensatoryLeavesYear_${index}`}
                        options={yearOptions.filter(
                          o =>
                            !remainingCompensatoryLeavesSelectedYear.some(
                              y => y === o.value,
                            ),
                        )}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingCompensatoryLeaves?.[index]?.year
                        }
                        onChange={(option: {value: string}) =>
                          onChange(option.value)
                        }
                        value={value ? {value: value, label: value} : null}
                      />
                    )}
                    name={`remainingCompensatoryLeaves.${index}.year`}
                    rules={{
                      validate: v => {
                        const rowData = watch(
                          `remainingCompensatoryLeaves.${index}`,
                        );
                        return Boolean(rowData?.availableDuration?.start) ||
                          Boolean(rowData?.availableDuration?.end) ||
                          Boolean(rowData?.availableTime)
                          ? Boolean(v) || '請填寫完整資訊'
                          : true;
                      },
                    }}
                  />
                </FormItem>
                <FormItem
                  label="可用期間(起)"
                  htmlFor={`remainingCompensatoryLeavesStart_${index}`}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <DatePicker
                        id={`remainingCompensatoryLeavesStart_${index}`}
                        selectsStart
                        dateFormat="yyyy/MM/dd"
                        selected={transferDateObj(value)}
                        onChange={async (date: Date) => {
                          onChange(
                            date
                              ? DateTime.fromJSDate(date).toLocaleString()
                              : null,
                          );
                          if (formState.isSubmitted) {
                            await trigger(
                              `remainingCompensatoryLeaves.${index}.availableDuration.end`,
                            );
                            await trigger(
                              `remainingCompensatoryLeaves.${index}.availableTime`,
                            );
                          }
                        }}
                        startDate={transferDateObj(value)}
                        endDate={transferDateObj(
                          watch(
                            `remainingCompensatoryLeaves.${index}.availableDuration.end`,
                          ),
                        )}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingCompensatoryLeaves?.[index]
                            ?.availableDuration?.start
                        }
                      />
                    )}
                    name={`remainingCompensatoryLeaves.${index}.availableDuration.start`}
                    control={control}
                    rules={{
                      validate: v => {
                        const rowData = watch(
                          `remainingCompensatoryLeaves.${index}`,
                        );
                        return Boolean(rowData?.availableDuration?.end) ||
                          Boolean(rowData?.year) ||
                          Boolean(rowData?.availableTime)
                          ? Boolean(v) || '請填寫完整資訊'
                          : true;
                      },
                    }}
                    defaultValue={field.availableDuration.start}
                  />
                  {errors?.remainingCompensatoryLeaves?.[index]
                    ?.availableDuration?.start && (
                    <ErrorMessage>
                      {
                        errors?.remainingCompensatoryLeaves?.[index]
                          ?.availableDuration?.start.message
                      }
                    </ErrorMessage>
                  )}
                </FormItem>
                <FormItem
                  label="可用期間(迄)"
                  htmlFor={`remainingCompensatoryLeavesEnd_${index}`}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <DatePicker
                        id={`remainingCompensatoryLeavesEnd_${index}`}
                        selectsEnd
                        dateFormat="yyyy/MM/dd"
                        selected={transferDateObj(value)}
                        onChange={async (date: Date) => {
                          onChange(
                            date
                              ? DateTime.fromJSDate(date).toLocaleString()
                              : null,
                          );
                          if (formState.isSubmitted) {
                            await trigger(
                              `remainingCompensatoryLeaves.${index}.availableDuration.start`,
                            );
                            await trigger(
                              `remainingCompensatoryLeaves.${index}.availableTime`,
                            );
                          }
                        }}
                        startDate={transferDateObj(
                          watch(
                            `remainingCompensatoryLeaves.${index}.availableDuration.start`,
                          ),
                        )}
                        endDate={transferDateObj(value)}
                        minDate={transferDateObj(
                          watch(
                            `remainingCompensatoryLeaves.${index}.availableDuration.start`,
                          ),
                        )}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingCompensatoryLeaves?.[index]
                            ?.availableDuration?.end
                        }
                      />
                    )}
                    name={`remainingCompensatoryLeaves.${index}.availableDuration.end`}
                    control={control}
                    rules={{
                      validate: {
                        isAfterStart: v => {
                          const startDate = watch(
                            `remainingCompensatoryLeaves.${index}.availableDuration.start`,
                          );
                          let startDateObj = transferDateObj(startDate);
                          let endDateObj = transferDateObj(v);
                          if (startDateObj && endDateObj) {
                            return (
                              DateTime.fromJSDate(startDateObj) <=
                                DateTime.fromJSDate(endDateObj) ||
                              '迄日不可早於起始日'
                            );
                          }
                          return true;
                        },
                        isAllFillIn: v => {
                          const rowData = watch(
                            `remainingCompensatoryLeaves.${index}`,
                          );
                          return Boolean(rowData?.availableDuration?.start) ||
                            Boolean(rowData?.year) ||
                            Boolean(rowData?.availableTime)
                            ? Boolean(v) || '請填寫完整資訊'
                            : true;
                        },
                      },
                    }}
                    defaultValue={field.availableDuration.end}
                  />
                  {errors?.remainingCompensatoryLeaves?.[index]
                    ?.availableDuration?.end && (
                    <ErrorMessage>
                      {
                        errors?.remainingCompensatoryLeaves?.[index]
                          ?.availableDuration?.end.message
                      }
                    </ErrorMessage>
                  )}
                </FormItem>
                <FormItem
                  label="可用時數"
                  htmlFor={`remainingCompensatoryLeavesAvailableTime_${index}`}
                >
                  <Controller
                    render={({onChange, value}) => (
                      <TimesInputNumber
                        autoComplete
                        onChange={async time => {
                          onChange(time);
                          if (formState.isSubmitted) {
                            await trigger(
                              `remainingCompensatoryLeaves.${index}.availableDuration.start`,
                            );
                            await trigger(
                              `remainingCompensatoryLeaves.${index}.availableDuration.end`,
                            );
                          }
                        }}
                        value={value}
                        error={
                          formState.isSubmitted &&
                          errors?.remainingCompensatoryLeaves?.[index]
                            ?.availableTime?.message
                        }
                        hourOptions={{
                          min: 0,
                          max: 999,
                          step: 1,
                        }}
                      />
                    )}
                    name={`remainingCompensatoryLeaves.${index}.availableTime`}
                    control={control}
                    rules={{
                      validate: v => {
                        const rowData = watch(
                          `remainingCompensatoryLeaves.${index}`,
                        );
                        return Boolean(rowData?.availableDuration?.start) ||
                          Boolean(rowData?.availableDuration?.end) ||
                          Boolean(rowData?.year)
                          ? Boolean(v) || '請填寫完整資訊'
                          : true;
                      },
                    }}
                    defaultValue={field.availableTime}
                  />
                </FormItem>
                {remainingCompensatoryLeavesField?.fields?.length > 1 && (
                  <CustomIconButton
                    onClick={() =>
                      remainingCompensatoryLeavesField.remove(index)
                    }
                    data-test-id={'remove-btn'}
                    icon="trash"
                    className="pt-[18px]"
                  />
                )}
              </div>
            );
          })}
        </div>
        <IconButton
          className={
            'w-[160px] text-[#353d45] border-[#353d45] hover:border-brand mt-3.5'
          }
          onClick={() => {
            remainingCompensatoryLeavesField.append(
              defaultCompensatoryLeavesField,
            );
          }}
        >
          <span
            style={{borderColor: 'inherit'}}
            className={cx(
              'w-[28px] h-[28px]',
              'flex items-center justify-center',
            )}
          >
            <FontAwesomeIcon
              icon={['fal', 'plus']}
              className={'!m-0 !text-xl'}
            />
          </span>
          新增剩餘補休
        </IconButton>
        <SubHeader>剩餘事假</SubHeader>
        <div className={'grid gap-5 grid-cols-[256px]'}>
          <FormItem
            label="可用時數"
            htmlFor={'remainingPersonalLeaveAvailableTime'}
          >
            <Controller
              render={({onChange, value}) => {
                return (
                  <TimesInputNumber
                    autoComplete
                    onChange={value => {
                      if (value) {
                        setValue(
                          'remainingPersonalLeave.year',
                          new Date().getFullYear(),
                        );
                      }
                      onChange(value);
                    }}
                    value={value}
                    hourOptions={{
                      min: 1,
                      max: 999,
                      step: 1,
                    }}
                    error={
                      formState.isSubmitted &&
                      errors?.remainingPersonalLeave?.availableTime?.message
                    }
                  />
                );
              }}
              name="remainingPersonalLeave.availableTime"
              control={control}
              defaultValue={remainingPersonalLeave.availableTime}
              rules={{
                validate: v => {
                  const rowData = watch(`remainingPersonalLeave`);
                  return Boolean(rowData?.year)
                    ? Boolean(v) || '請填寫完整資訊'
                    : true;
                },
              }}
            />
          </FormItem>
        </div>
        <SubHeader>剩餘有薪病假</SubHeader>
        <div className={'grid grid-cols-[256px] gap-5'}>
          <FormItem
            label="可用時數"
            htmlFor={'remainingSickLeaveAvailableTime'}
          >
            <Controller
              render={({onChange, value}) => (
                <TimesInputNumber
                  autoComplete
                  onChange={value => {
                    if (value) {
                      setValue(
                        'remainingSickLeave.year',
                        new Date().getFullYear(),
                      );
                    }
                    onChange(value);
                  }}
                  value={value}
                  hourOptions={{
                    min: 1,
                    max: 999,
                    step: 1,
                  }}
                  error={
                    formState.isSubmitted &&
                    errors?.remainingSickLeave?.availableTime?.message
                  }
                />
              )}
              name="remainingSickLeave.availableTime"
              control={control}
              defaultValue={remainingSickLeave.availableTime}
              rules={{
                validate: v => {
                  const rowData = watch(`remainingSickLeave`);
                  return Boolean(rowData?.year)
                    ? Boolean(v) || '請填寫完整資訊'
                    : true;
                },
              }}
            />
          </FormItem>
        </div>
      </DialogContent>
      <DialogFooter>
        <ButtonGroup>
          <Button variant={'primary'} onClick={onClose} className={'close-btn'}>
            取消
          </Button>
          <Button
            variant={'cta'}
            onClick={handleSubmit(onSubmit)}
            className={'confirm-btn'}
          >
            儲存
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};

export default Form;
